import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PersonDetailPopover from '../components/PersonDetailPopover';
import CompanySearchResults from '../components/CompanySearchResults';
import ExecutiveDatabase from '../components/ExecutiveDatabase';

const DecisionMaker = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sheetData, setSheetData] = useState([]);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showPopover, setShowPopover] = useState(false);
  const [apolloJobs, setApolloJobs] = useState({}); // Track Apollo job status for each person
  
  // New states for company search workflow
  const [companyResults, setCompanyResults] = useState([]);
  const [showCompanyResults, setShowCompanyResults] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companySearchLoading, setCompanySearchLoading] = useState(false);
  const [isCompanyListCollapsed, setIsCompanyListCollapsed] = useState(false);

  // Backend API URL configuration
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Google Sheets configuration
  const GOOGLE_SHEETS_API_KEY = process.env.REACT_APP_GOOGLE_SHEETS_API_KEY || 'YOUR_API_KEY_HERE';
  const SPREADSHEET_ID = process.env.REACT_APP_SPREADSHEET_ID || 'YOUR_SPREADSHEET_ID_HERE';
  const RANGE = 'decision_maker!A:Z'; // Adjust range as needed

  // Fetch Google Sheets data
  const fetchSheetData = async () => {
    setSheetLoading(true);
    try {
      const url = `https://sheets.googleapis.com/v4/spreadsheets/${SPREADSHEET_ID}/values/${RANGE}?key=${GOOGLE_SHEETS_API_KEY}`;
      const response = await axios.get(url);
      
      if (response.data.values && response.data.values.length > 0) {
        const [headers, ...rows] = response.data.values;
        const formattedData = rows.map(row => {
          const rowObject = {};
          headers.forEach((header, index) => {
            rowObject[header] = row[index] || '';
          });
          return rowObject;
        });
        // Reverse the order so last row in sheet appears first in web app
        setSheetData(formattedData.reverse());
        setFilteredData(formattedData.reverse());
      }
    } catch (err) {
      console.error('Error fetching Google Sheets data:', err);
      setError('Failed to load data from Google Sheets');
    } finally {
      setSheetLoading(false);
    }
  };

  // Load sheet data on component mount
  useEffect(() => {
    fetchSheetData();
  }, []);

  // Filter data based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredData([...sheetData].reverse());
      setCurrentPage(1);
    } else {
      const filtered = sheetData.filter(row => 
        Object.values(row).some(value => 
          value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setFilteredData([...filtered].reverse());
      setCurrentPage(1);
    }
  }, [searchTerm, sheetData]);

  const handleCompanySearch = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    setCompanySearchLoading(true);
    setError(null);
    setCompanyResults([]);
    setShowCompanyResults(false);
    setSelectedCompany(null);
    setResult(null);

    try {
      const response = await axios.post(`${API_URL}/api/company-search`, {
        company_name: input.trim(),
        timestamp: new Date().toISOString(),
      });
      
      // The response should contain an array of companies or a single company
      let companies = [];
      if (Array.isArray(response.data)) {
        companies = response.data;
      } else if (response.data.companies && Array.isArray(response.data.companies)) {
        companies = response.data.companies;
      } else if (response.data.id) {
        // Single company response
        companies = [response.data];
      } else {
        throw new Error('Invalid response format from company search');
      }
      
      setCompanyResults(companies);
      setShowCompanyResults(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to search for companies');
    } finally {
      setCompanySearchLoading(false);
    }
  };

  const handleCompanySelect = async (company) => {
  setSelectedCompany(company);
  setIsCompanyListCollapsed(true); // Auto-collapse after selection
  setLoading(true);
  setError(null);
  setResult(null);

  try {
    const response = await axios.post(`${API_URL}/api/decision-maker`, {
      company_id: company.id,
      company_name: company.name,
      timestamp: new Date().toISOString(),
    });
    setResult(response.data);
  } catch (err) {
    setError(err.response?.data?.error || 'Failed to process decision maker request');
  } finally {
    setLoading(false);
  }
};

  const resetSearch = () => {
    setInput('');
    setCompanyResults([]);
    setShowCompanyResults(false);
    setSelectedCompany(null);
    setResult(null);
    setError(null);
    setIsCompanyListCollapsed(false);
  };

  // Handle row click to show person details
  const handleRowClick = (person) => {
    setSelectedPerson(person);
    setShowPopover(true);
  };

  // Close popover
  const closePopover = () => {
    setShowPopover(false);
    setSelectedPerson(null);
  };

  // Apollo job tracking functions
  const startApolloJob = async (person) => {
    const personId = person.id || person.linkedin_url || `${person.name}-${person.current_employer}`;
    
    try {
      // Set job as pending
      setApolloJobs(prev => ({
        ...prev,
        [personId]: { status: 'pending', jobId: null, error: null, result: null }
      }));

      const response = await axios.post(`${API_URL}/api/apollo`, {
        linkedin_url: person.linkedin_url,
        id: person.id || '',
        name: person.name || person.full_name || '',
        company: person.current_employer || '',
        timestamp: new Date().toISOString()
      });

      console.log('Apollo search started:', response.data);

      // Update with job ID
      setApolloJobs(prev => ({
        ...prev,
        [personId]: { 
          status: 'pending', 
          jobId: response.data.jobId, 
          error: null, 
          result: null,
          estimatedTime: response.data.estimatedTime 
        }
      }));

      // Start polling for job status
      pollApolloJobStatus(response.data.jobId, personId);

    } catch (error) {
      console.error('Error starting Apollo search:', error);
      setApolloJobs(prev => ({
        ...prev,
        [personId]: { 
          status: 'error', 
          jobId: null, 
          error: error.response?.data?.error || 'Failed to start Apollo search',
          result: null 
        }
      }));
    }
  };

  const pollApolloJobStatus = async (jobId, personId) => {
    const startTime = Date.now();
    const TIMEOUT_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
    
    const pollInterval = setInterval(async () => {
      try {
        // Check if 5 minutes have passed
        if (Date.now() - startTime > TIMEOUT_DURATION) {
          clearInterval(pollInterval);
          setApolloJobs(prev => ({
            ...prev,
            [personId]: {
              ...prev[personId],
              status: 'error',
              error: 'Job polling timed out after 5 minutes'
            }
          }));
          console.log(`Apollo job ${jobId} polling timed out after 5 minutes`);
          return;
        }

        const response = await axios.get(`${API_URL}/api/apollo-job-status/${jobId}`);
        const jobData = response.data;

        setApolloJobs(prev => ({
          ...prev,
          [personId]: {
            status: jobData.status,
            jobId: jobData.jobId,
            error: jobData.error,
            result: jobData.result,
            completedAt: jobData.completedAt
          }
        }));

        if (jobData.status === 'completed' || jobData.status === 'error') {
          clearInterval(pollInterval);
          
          if (jobData.status === 'completed') {
            console.log('Apollo job completed, refreshing sheet data...');
            // Refresh sheet data to show updated results
            await fetchSheetData();
            console.log('Sheet data refreshed successfully');
          }
        }

      } catch (error) {
        console.error('Error polling Apollo job status:', error);
        clearInterval(pollInterval);
        setApolloJobs(prev => ({
          ...prev,
          [personId]: {
            ...prev[personId],
            status: 'error',
            error: 'Failed to check job status'
          }
        }));
      }
    }, 3000); // Poll every 3 seconds


  };

  const getApolloJobStatus = (person) => {
    const personId = person.id || person.linkedin_url || `${person.name}-${person.current_employer}`;
    return apolloJobs[personId] || { status: 'idle' };
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent mb-6">
            👑 Elite Decision Makers
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Connect with influential decision makers and C-suite executives across industries
          </p>
        </div>

        {/* Main Form Card */}
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 rounded-3xl shadow-2xl p-8 mb-12 backdrop-blur-sm">
          <form onSubmit={handleCompanySearch} className="space-y-8">
            <div>
              <label htmlFor="query" className="block text-lg font-semibold text-yellow-400 mb-4">
                Enter company name to search for executives
              </label>
              <div className="relative">
                <input
                  id="query"
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="e.g., Tesla, JPMorgan Chase, Microsoft, Goldman Sachs..."
                  className="w-full px-6 py-4 bg-black/50 border-2 border-yellow-500/30 rounded-2xl text-white placeholder-gray-400 focus:ring-4 focus:ring-yellow-500/20 focus:border-yellow-500 transition-all duration-300 text-lg backdrop-blur-sm"
                  required
                />
                {(showCompanyResults || selectedCompany) && (
                  <button
                    type="button"
                    onClick={resetSearch}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-yellow-400 transition-colors duration-200 text-xl"
                    title="Reset search"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            
            <button
              type="submit"
              disabled={companySearchLoading || !input.trim()}
              className={`group relative w-full py-5 px-8 rounded-2xl font-bold text-lg transition-all duration-300 transform focus:outline-none overflow-hidden ${
                companySearchLoading || !input.trim()
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-500 text-black hover:from-yellow-400 hover:via-yellow-500 hover:to-yellow-400 hover:scale-[1.02] hover:shadow-2xl hover:shadow-yellow-500/30 active:scale-[0.98]'
              }`}
            >
              {!companySearchLoading && !input.trim() && (
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/0 via-yellow-400/10 to-yellow-400/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></div>
              )}
              {companySearchLoading || !input.trim() || (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-opacity duration-300"></div>
              )}
              <span className="relative z-10">
                {companySearchLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-6 h-6 border-3 border-yellow-400 border-t-transparent rounded-full animate-spin mr-3"></div>
                    Searching Companies...
                  </div>
                ) : (
                  'Search Companies'
                )}
              </span>
            </button>
          </form>
        </div>

        {/* Company Results */}
        <CompanySearchResults 
        showCompanyResults={showCompanyResults}
        companyResults={companyResults}
        handleCompanySelect={handleCompanySelect}
        input={input}
        resetSearch={resetSearch}
        isCollapsed={isCompanyListCollapsed}
        setIsCollapsed={setIsCompanyListCollapsed}
      />

        {/* Selected Company Info */}
        {selectedCompany && loading && (
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 rounded-3xl shadow-2xl my-12 p-8 animate-slide-up backdrop-blur-sm">
            <div className="flex items-center justify-center py-8">
              <div className="w-10 h-10 border-3 border-yellow-500 border-t-transparent rounded-full animate-spin mr-4"></div>
              <div>
                <h3 className="text-yellow-300 text-lg font-semibold">Processing {selectedCompany.name}</h3>
                <p className="text-gray-300">Finding decision makers...</p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-red-900/20 to-red-800/20 border border-red-500/30 p-6 mb-8 rounded-2xl animate-slide-up backdrop-blur-sm">
            <div className="flex items-center">
              <div className="text-red-400 mr-4 text-2xl">⚠️</div>
              <div>
                <h3 className="text-red-300 font-semibold text-lg">Error</h3>
                <p className="text-red-200">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {result && selectedCompany && (
          <div className="max-w-5xl mx-auto bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 rounded-3xl shadow-2xl my-12 p-8 animate-slide-up backdrop-blur-sm">
            <h2 className="text-3xl font-bold text-yellow-400 mb-6 flex items-center">
              <span className="text-yellow-500 mr-4">✨</span>
              Executive Intelligence Report - {selectedCompany.name}
            </h2>
            
            {/* Company Info Banner */}
            <div className="bg-gradient-to-r from-yellow-500/10 to-yellow-600/10 rounded-2xl p-6 mb-6 border border-yellow-500/20">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-yellow-400 font-semibold">Company:</span>
                  <p className="text-white font-bold text-lg">{selectedCompany.name}</p>
                </div>
                {selectedCompany.industry && (
                  <div>
                    <span className="text-yellow-400 font-semibold">Industry:</span>
                    <p className="text-white">{selectedCompany.industry}</p>
                  </div>
                )}
                {selectedCompany.region && (
                  <div>
                    <span className="text-yellow-400 font-semibold">Region:</span>
                    <p className="text-white">{selectedCompany.region}</p>
                  </div>
                )}
                {selectedCompany.email_domain && (
                  <div>
                    <span className="text-yellow-400 font-semibold">Email Domain:</span>
                    <p className="text-white">{selectedCompany.email_domain}</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-black/40 rounded-2xl p-6 border border-yellow-500/10">
              <pre className="text-gray-200 whitespace-pre-wrap text-sm leading-relaxed font-mono">
                {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Google Sheets Data */}
        <ExecutiveDatabase 
          sheetData={sheetData}
          sheetLoading={sheetLoading}
          filteredData={filteredData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          rowsPerPage={rowsPerPage}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleRowClick={handleRowClick}
          fetchSheetData={fetchSheetData}
        />

        {/* Person Detail Popover */}
        {showPopover && selectedPerson && (
          <PersonDetailPopover 
            person={selectedPerson} 
            onClose={closePopover}
            sheetData={sheetData}
            API_URL={API_URL}
            startApolloJob={startApolloJob}
            getApolloJobStatus={getApolloJobStatus}
            fetchSheetData={fetchSheetData}
          />
        )}
      </div>
    </div>
  );
};

export default DecisionMaker;